import { useRef, FC, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import { BillingSettingsEntry, useUpgradeModal } from '@/features/billing';
import { InviteWidget } from '@/features/referral';
import { SecuritySettingsCard } from '@/features/auth';
import { useAuthStore } from '@/stores/useAuthStore';

interface SystemViewProps {
    onFullBackup: () => void;
    onFullRestore: (e: ChangeEvent<HTMLInputElement>) => void;
    onResetSystem: () => void;
    onImportPriceList: (e: ChangeEvent<HTMLInputElement>) => void;
    onExportPriceList: () => void;
}

export const SystemView: FC<SystemViewProps> = ({
    onFullBackup,
    onFullRestore,
    onResetSystem,
    onImportPriceList,
    onExportPriceList
}) => {
    const { t } = useTranslation();
    const backupInputRef = useRef<HTMLInputElement>(null);
    const priceListInputRef = useRef<HTMLInputElement>(null);
    const { entitled, planStatus, currentPeriodEnd } = useAuthStore();
    const { openUpgrade } = useUpgradeModal();

    const handleUpgradeClick = () => openUpgrade('sync');

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
            {/* Backup & Restore */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-xl text-purple-600 dark:text-purple-400">
                        <RefreshCw size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">{t('system.migration.title', 'Миграция данных')}</h3>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">{t('system.migration.subtitle', 'Перенос всего приложения на другое устройство')}</p>
                    </div>
                </div>

                <button 
                    onClick={onFullBackup}
                    className="w-full py-4 border-2 border-purple-50 dark:border-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-100 dark:hover:border-purple-800 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all"
                >
                    <Download size={20}/> {t('system.migration.download_btn', 'Скачать полный бэкап')}
                </button>

                <div className="relative">
                    <button 
                        onClick={() => backupInputRef.current?.click()}
                        className="w-full py-4 bg-purple-600 text-white rounded-2xl font-bold shadow-lg shadow-purple-100 dark:shadow-none hover:bg-purple-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                        <Upload size={20}/> {t('system.migration.restore_btn', 'Восстановить из файла')}
                    </button>
                    <input 
                        ref={backupInputRef} 
                        type="file" 
                        accept=".json" 
                        onChange={onFullRestore} 
                        className="hidden" 
                    />
                </div>
            </div>

            {/* Excel Price List */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-xl text-green-600 dark:text-green-400">
                        <FileText size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">{t('system.excel.title', 'Excel Прайс-лист')}</h3>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">{t('system.excel.subtitle', 'Массовое редактирование цен через Excel')}</p>
                    </div>
                </div>

                <button 
                    onClick={onExportPriceList}
                    className="w-full py-4 border-2 border-green-50 dark:border-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-100 dark:hover:border-green-800 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all"
                >
                    <Download size={20}/> {t('system.excel.download_btn', 'Скачать шаблон (XLSX)')}
                </button>

                <div className="relative">
                    <button 
                        onClick={() => priceListInputRef.current?.click()}
                        className="w-full py-4 bg-green-600 text-white rounded-2xl font-bold shadow-lg shadow-green-100 dark:shadow-none hover:bg-green-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                        <Upload size={20}/> {t('system.excel.import_btn', 'Загрузить цены')}
                    </button>
                    <input 
                        ref={priceListInputRef} 
                        type="file" 
                        accept=".xlsx" 
                        onChange={onImportPriceList} 
                        className="hidden" 
                    />
                </div>
            </div>

            {/* Phase 43 (PWD-08): Sign-in & Security — set/change password entry point */}
            <div className="md:col-span-2">
                <SecuritySettingsCard />
            </div>

            {/* Billing — plan status and self-service management (08-05) */}
            <div className="md:col-span-2">
                <BillingSettingsEntry
                    entitled={entitled}
                    planStatus={planStatus ?? undefined}
                    currentPeriodEnd={currentPeriodEnd ?? undefined}
                    onUpgradeClick={handleUpgradeClick}
                />
            </div>

            {/* REFUI-01: InviteWidget — always expanded in Settings (alwaysExpanded) */}
            <div className="md:col-span-2">
                <InviteWidget alwaysExpanded />
            </div>

            {/* Danger Zone */}
            <div className="md:col-span-2 mt-4 p-6 bg-red-50/50 dark:bg-red-900/10 rounded-3xl border border-red-100 dark:border-red-900/20 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-white dark:bg-slate-800 p-3 rounded-full text-red-500 shadow-sm">
                        <AlertTriangle size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-red-900 dark:text-red-500">{t('system.danger.title', 'Сброс к заводским настройкам')}</h3>
                        <p className="text-xs text-red-700 dark:text-red-400/70 font-medium max-w-sm">
                            {t('system.danger.subtitle', 'Внимание: это действие безвозвратно удалит все проекты и вернет базу услуг в исходное состояние.')}
                        </p>
                    </div>
                </div>
                <button 
                    onClick={onResetSystem}
                    className="px-6 py-3 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-600 dark:hover:bg-red-600 hover:text-white rounded-xl font-bold text-sm transition-all shadow-sm"
                >
                    {t('system.danger.reset_btn', 'Выполнить сброс')}
                </button>
            </div>
        </div>
    );
};
